import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { Tooltip } from '../components/ui/Tooltip';
import { Textarea } from '../components/ui/Textarea';
import { GenerateActivityWizardModal } from '../components/operations/GenerateActivityWizardModal';
import { ActivityProgressBadge } from '../components/operations/ActivityProgressBadge';
import { ActivityDetailModal } from '../components/operations/ActivityDetailModal';
import { operativeActivitiesStore, getProgress, OperativeActivity } from '../services/operativeActivities';
import { Plus, Filter, Download, Eye, Edit, Search, FileText, Euro, Calendar, LayoutGrid, List as ListIcon, AlertTriangle, CheckCircle2, X, Save, Trash2, AlertCircle, ClipboardList } from 'lucide-react';
import { useRBAC } from '../contexts/RBACContext';
import { PermissionGate } from '../components/PermissionGate';
import { ProgressiveNumberInput } from '../components/ProgressiveNumberInput';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { mockData } from '../services/mockData';
import { getOfferProductMetadata, offerProductOptions, offerProductTypes, offerScopes } from '../services/offerCatalog';
import { motion, AnimatePresence } from 'framer-motion';
const initialContractsData = mockData.getContracts();
const columnsConfig = [{
  key: 'number',
  label: 'Codice'
}, {
  key: 'client',
  label: 'Cliente'
}, {
  key: 'product',
  label: 'Prodotto'
}, {
  key: 'status',
  label: 'Stato'
}, {
  key: 'amount',
  label: 'Importo'
}, {
  key: 'expiry',
  label: 'Scadenza'
}, {
  key: 'actions',
  label: 'Azioni'
}];
// Updated Kanban columns as per specs
const kanbanColumns = [{
  id: 'da_proporre',
  title: 'Da proporre',
  color: 'bg-slate-50 border-slate-200'
}, {
  id: 'in_valutazione',
  title: 'In valutazione',
  color: 'bg-blue-50 border-blue-200'
}, {
  id: 'da_sollecitare',
  title: 'Da sollecitare',
  color: 'bg-yellow-50 border-yellow-200'
}, {
  id: 'accettato',
  title: 'Accettato',
  color: 'bg-green-50 border-green-200'
}, {
  id: 'non_accettato',
  title: 'Non accettato',
  color: 'bg-red-50 border-red-200'
}, {
  id: 'revocato',
  title: 'Revocato',
  color: 'bg-orange-50 border-orange-200'
}];
const statusLabels: Record<string, string> = {
  da_proporre: 'Da proporre',
  in_valutazione: 'In valutazione',
  da_sollecitare: 'Da sollecitare',
  accettato: 'Accettato',
  non_accettato: 'Non accettato',
  revocato: 'Revocato',
  // Legacy mappings
  proposal: 'Da proporre',
  pending: 'In valutazione',
  urgent: 'Da sollecitare',
  accepted: 'Accettato',
  rejected: 'Non accettato',
  revoked: 'Revocato'
};
export function ContractsPage() {
  const navigate = useNavigate();
  const {
    canCreate,
    canEdit
  } = useRBAC();
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>('kanban');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [kanbanData, setKanbanData] = useState(initialContractsData);
  const [draggedCard, setDraggedCard] = useState<any>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [isGenerateActivityOpen, setIsGenerateActivityOpen] = useState(false);
  const [editFormStatus, setEditFormStatus] = useState('');
  const [editFormAcceptanceDate, setEditFormAcceptanceDate] = useState('');
  const [editFormRejectionDate, setEditFormRejectionDate] = useState('');
  const [editFormRejectionReason, setEditFormRejectionReason] = useState('');
  const [editFormRevokeDate, setEditFormRevokeDate] = useState('');
  const [editFormRevokeReason, setEditFormRevokeReason] = useState('');
  const [offerProduct, setOfferProduct] = useState('');
  const [offerType, setOfferType] = useState('');
  const [offerProductCode, setOfferProductCode] = useState('');
  const [offerScope, setOfferScope] = useState('');
  const [selectedOperativeActivity, setSelectedOperativeActivity] = useState<OperativeActivity | null>(null);
  const [isActivityDetailOpen, setIsActivityDetailOpen] = useState(false);
  const [, setActivityRefresh] = useState(0);
  const [contractActivities, setContractActivities] = useState<OperativeActivity[]>([]);
  const [selectedActivityForDetail, setSelectedActivityForDetail] = useState<OperativeActivity | null>(null);
  const [showActivityDetailModal, setShowActivityDetailModal] = useState(false);
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(kanbanData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, ['number', 'client']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  const handleProductSelection = (product: string) => {
    const metadata = getOfferProductMetadata(product);
    setOfferProduct(product);
    setOfferType(metadata?.type || '');
    setOfferProductCode(metadata?.code || '');
  };
  const handleView = (item: any) => {
    setSelectedItem(item);
    // Load activities for this contract
    const activities = operativeActivitiesStore.getByContract(item.number);
    setContractActivities(activities);
    setIsViewModalOpen(true);
  };
  const handleEdit = (item: any) => {
    const metadata = getOfferProductMetadata(item.product || '');
    setSelectedItem(item);
    setOfferProduct(item.product || '');
    setOfferType(item.tipologia || metadata?.type || '');
    setOfferProductCode(item.codiceProdotto || metadata?.code || '');
    setOfferScope(item.ambito || '');
    setEditFormStatus(item.status || 'da_proporre');
    setEditFormAcceptanceDate(item.acceptanceDate || '');
    setEditFormRejectionDate(item.rejectionDate || '');
    setEditFormRejectionReason(item.rejectionReason || '');
    setEditFormRevokeDate(item.revokeDate || '');
    setEditFormRevokeReason(item.revokeReason || '');
    setIsModalOpen(true);
  };
  const handleCreate = () => {
    setSelectedItem(null);
    setOfferProduct('');
    setOfferType('');
    setOfferProductCode('');
    setOfferScope('');
    setEditFormStatus('da_proporre');
    setEditFormAcceptanceDate('');
    setEditFormRejectionDate('');
    setEditFormRejectionReason('');
    setEditFormRevokeDate('');
    setEditFormRevokeReason('');
    setIsModalOpen(true);
  };
  const handleDelete = (item: any) => {
    setItemToDelete(item);
    setShowDeleteDialog(true);
  };
  const confirmDelete = () => {
    setDeleteSuccess(true);
    setTimeout(() => {
      setDeleteSuccess(false);
      setShowDeleteDialog(false);
      setItemToDelete(null);
    }, 1500);
  };
  const handleSave = () => {
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setIsModalOpen(false);
    }, 1500);
  };
  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, card: any) => {
    setDraggedCard(card);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const handleDrop = (e: React.DragEvent, targetColumnId: string) => {
    e.preventDefault();
    if (draggedCard && draggedCard.status !== targetColumnId) {
      setKanbanData(prev => prev.map(item => item.id === draggedCard.id ? {
        ...item,
        status: targetColumnId
      } : item));
    }
    setDraggedCard(null);
  };
  const getCardsByColumn = (columnId: string) => {
    const statusMap: Record<string, string[]> = {
      da_proporre: ['da_proporre', 'proposal'],
      in_valutazione: ['in_valutazione', 'pending'],
      da_sollecitare: ['da_sollecitare', 'urgent'],
      accettato: ['accettato', 'accepted'],
      non_accettato: ['non_accettato', 'rejected'],
      revocato: ['revocato', 'revoked']
    };
    const validStatuses = statusMap[columnId] || [columnId];
    return searchResult.filter(c => validStatuses.includes(c.status));
  };
  const handleActivityCreated = (activityId: number) => {
    // Refresh activities list
    if (selectedItem) {
      const activities = operativeActivitiesStore.getByContract(selectedItem.number);
      setContractActivities(activities);
    }
  };
  const handleViewActivityDetail = (activity: OperativeActivity) => {
    setSelectedActivityForDetail(activity);
    setShowActivityDetailModal(true);
  };
  const handleStepComplete = (activityId: string, stepIndex: number) => {
    operativeActivitiesStore.completeStep(activityId, stepIndex);
    const updated = operativeActivitiesStore.getById(activityId);
    if (updated) setSelectedOperativeActivity({
      ...updated
    });
    setActivityRefresh(r => r + 1);
  };
  const handleEditActivityFromContract = (activity: OperativeActivity) => {
    setIsActivityDetailOpen(false);
    // Open the edit form for the activity
    // Find the corresponding contract to provide context
    const matchingContract = kanbanData.find(c => c.number === activity.contractNumber);
    if (matchingContract) {
      setSelectedItem(matchingContract);
      setEditFormStatus(matchingContract.status || 'da_proporre');
      setEditFormAcceptanceDate(matchingContract.acceptanceDate || '');
      setEditFormRejectionDate(matchingContract.rejectionDate || '');
      setEditFormRejectionReason(matchingContract.rejectionReason || '');
      setEditFormRevokeDate(matchingContract.revokeDate || '');
      setEditFormRevokeReason(matchingContract.revokeReason || '');
      setIsModalOpen(true);
    }
  };
  return <Layout data-id="element-3738" data-mp="14t6z8h0hijm6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-3739" data-mp="14pj4jejazr5v">
        <div data-id="element-3740" data-mp="18qlaee7q71gt">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-3741" data-mp="2taqdpiwo4flj">
            Offerte e contratti
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-3742" data-mp="2p8vb8b303w66">
            Gestione del ciclo di vita dei contratti e delle offerte.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-3743" data-mp="2kak262kj5pp0">
          <div className="bg-slate-100 p-1 rounded-xl flex items-center" data-id="element-3744" data-mp="2fb77ivnf3ebv">
            <button onClick={() => setViewMode('kanban')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'kanban' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-3745" data-mp="2fdbk01zdtlnr">
              <LayoutGrid className="h-4 w-4 inline-block mr-1.5" data-id="element-3746" data-mp="337lanxdam7xp" /> Kanban
            </button>
            <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'list' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-3747" data-mp="2y9jqta81m4l6">
              <ListIcon className="h-4 w-4 inline-block mr-1.5" data-id="element-3748" data-mp="1p0ycob6mknya" /> Lista
            </button>
          </div>
          <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-3750" data-mp="1fc6n29p1efqj" />} data-id="element-3749" data-mp="1t1qkczzumksg">
            Esporta
          </Button>
          <PermissionGate module="contracts" action="create" data-id="element-3751" data-mp="1k9ontcswhong">
            <Button onClick={handleCreate} startContent={<Plus className="h-4 w-4" data-id="element-3753" data-mp="15bnyx2qfwd2t" />} data-id="element-3752" data-mp="1k858bof2jhmq">
              Nuova offerta
            </Button>
          </PermissionGate>
        </div>
      </div>

      <Card className="mb-6" noPadding data-id="element-3754" data-mp="wd2fz6h22w1i">
        <div className="p-4 flex flex-col gap-4" data-id="element-3755" data-mp="we3u5jtdbyng">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-3756" data-mp="11bcglwljuoui">
            <div className="flex-1" data-id="element-3757" data-mp="11dq6lr1dhav7">
              <Input placeholder="Cerca contratto, cliente..." startContent={<Search className="h-4 w-4" data-id="element-3759" data-mp="2vpd9m0uf4hmo" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-3758" data-mp="2qqtqe877ljy0" />
            </div>
            <div className="flex gap-2" data-id="element-3760" data-mp="30uqs7vy5swt2">
              <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-3762" data-mp="39udmzywdv4d5" />} data-id="element-3761" data-mp="30w1yc2f2ugnc">
                Filtri{' '}
                {Object.keys(filters).filter(k => filters[k]).length > 0 && <Badge variant="primary" size="sm" className="ml-1" data-id="element-3763" data-mp="3jli1kxi5cn7g">
                    {Object.keys(filters).filter(k => filters[k]).length}
                  </Badge>}
              </Button>
              {viewMode === 'list' && <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="contracts" lockedColumns={['number']} data-id="element-3764" data-mp="3th1v5d98n9t1" />}
            </div>
          </div>

          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-3765" data-mp="25kc6vymu2pyz">
              <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'da_proporre',
            label: 'Da proporre'
          }, {
            value: 'in_valutazione',
            label: 'In valutazione'
          }, {
            value: 'da_sollecitare',
            label: 'Da sollecitare'
          }, {
            value: 'accettato',
            label: 'Accettato'
          }, {
            value: 'non_accettato',
            label: 'Non accettato'
          }, {
            value: 'revocato',
            label: 'Revocato'
          }]} data-id="element-3766" data-mp="29l5vyvc0iatg" />
              <div className="col-span-full flex justify-end" data-id="element-3767" data-mp="hppa78h8c7br">
                <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-3769" data-mp="2y6r31t2pwo6" />} data-id="element-3768" data-mp="cpb7m5yedpl4">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      {viewMode === 'list' ? <Card noPadding className="overflow-hidden" data-id="element-3770" data-mp="3q24m08ffcxi7">
          <div className="overflow-x-auto" data-id="element-3771" data-mp="3l4l6z6awkgp7">
            <table className="w-full text-left border-collapse table-auto" data-id="element-3772" data-mp="3l5nvx8oo092h">
              <thead data-id="element-3773" data-mp="3bgh998bjwv9d">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-3774" data-mp="3bfxoljuinsrn">
                  {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-4 py-4 ${col.key === 'actions' ? 'text-left pr-6' : ''}`} data-id="element-3775" data-mp="36gungahnq6oy">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-3776" data-mp="3kgdv726k3g0q">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-3777" data-mp="3pf75ymugksx7">
                    {visibleColumns.includes('number') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-3778" data-mp="lsq3xpb3e7v6">
                        {item.number}
                      </td>}
                    {visibleColumns.includes('client') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-3779" data-mp="2fa4f5bdqfnni">
                        {item.client}
                      </td>}
                    {visibleColumns.includes('product') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-3780" data-mp="1ni6j1j0i5q7f">
                        {item.product}
                      </td>}
                    {visibleColumns.includes('status') && <td className="px-4 py-4" data-id="element-3781" data-mp="1wl7ln9q5sz6c">
                        <Badge variant={['accettato', 'accepted'].includes(item.status) ? 'success' : ['in_valutazione', 'pending', 'da_sollecitare', 'urgent'].includes(item.status) ? 'warning' : ['non_accettato', 'rejected', 'revocato', 'revoked'].includes(item.status) ? 'danger' : 'neutral'} data-id="element-3782" data-mp="1rktctpgqhl79">
                          {statusLabels[item.status] || item.status}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('amount') && <td className="px-4 py-4 text-sm font-mono text-slate-900" data-id="element-3783" data-mp="2letxli76jqdt">
                        {item.amount}
                      </td>}
                    {visibleColumns.includes('expiry') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-3784" data-mp="2d3bfu2fyfixr">
                        {item.expiry}
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-3785" data-mp="2446iv0k1jxnp">
                        <div className="flex items-center gap-1" data-id="element-3786" data-mp="241bzzdgoekwe">
                          <Tooltip content="Visualizza contratto" data-id="element-3787" data-mp="30sw1it3hsuaq">
                            <Button variant="light" size="sm" className="!px-1" onClick={() => handleView(item)} data-id="element-3788" data-mp="2vu2xrse0chnr">
                              <Eye className="h-4 w-4 text-slate-500" data-id="element-3789" data-mp="1gdklc37nxvry" />
                            </Button>
                          </Tooltip>
                          <PermissionGate module="contracts" action="edit" data-id="element-3790" data-mp="1bdg245fdemy1">
                            <Tooltip content="Modifica contratto" data-id="element-3791" data-mp="bqj32edt1j6m">
                              <Button variant="light" size="sm" className="!px-1" onClick={() => handleEdit(item)} data-id="element-3792" data-mp="bru6ue792qxq">
                                <Edit className="h-4 w-4 text-seform-orange" data-id="element-3793" data-mp="3u5jtf6fnw761" />
                              </Button>
                            </Tooltip>
                          </PermissionGate>
                          <PermissionGate module="contracts" action="delete" data-id="element-3794" data-mp="qnrwreqkyz1i">
                            <Tooltip content="Elimina contratto" data-id="element-3795" data-mp="ln4at7h7l8e5">
                              <Button variant="light" size="sm" className="!px-1" onClick={() => handleDelete(item)} data-id="element-3796" data-mp="gpcg65p6j869">
                                <Trash2 className="h-4 w-4 text-red-500" data-id="element-3797" data-mp="31ho2kicrx9h6" />
                              </Button>
                            </Tooltip>
                          </PermissionGate>
                        </div>
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-3798" data-mp="3612gl8p1g2s1">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-3799" data-mp="3a24hdtylsmw4" />
          </div>
        </Card> :
    // Kanban View with fixed scroll
    <div className="overflow-x-auto pb-4" data-id="element-3800" data-mp="3oymwhq9trr0i">
          <div className="flex gap-4 min-w-max" data-id="element-3801" data-mp="1pmansunzcti7">
            {kanbanColumns.map(column => {
          const cards = getCardsByColumn(column.id);
          return <div key={column.id} className={`w-72 flex-shrink-0 rounded-xl border ${column.color} flex flex-col max-h-[calc(100vh-280px)]`} onDragOver={handleDragOver} onDrop={e => handleDrop(e, column.id)} data-id="element-3802" data-mp="1umyne4q9yio1">
                  <div className="p-3 border-b border-slate-200/50 flex items-center justify-between" data-id="element-3803" data-mp="1rjirh7hlzwee">
                    <h3 className="font-semibold text-slate-700 text-sm" data-id="element-3804" data-mp="25jaaxnqwa4zs">
                      {column.title}
                    </h3>
                    <Badge variant="neutral" size="sm" data-id="element-3805" data-mp="20j5v7zxt9eam">
                      {cards.length}
                    </Badge>
                  </div>
                  <div className="flex-1 overflow-y-auto p-2 space-y-2" data-id="element-3806" data-mp="2fb690yszdv40">
                    {cards.map(card => <div key={card.id} draggable onDragStart={e => handleDragStart(e, card)} onClick={() => handleView(card)} className={`bg-white rounded-lg p-3 shadow-sm border border-slate-100 cursor-pointer hover:shadow-md transition-all ${draggedCard?.id === card.id ? 'opacity-50' : ''}`} data-id="element-3807" data-mp="2o9rscw6a1e5u">
                        <div className="flex items-start justify-between mb-2" data-id="element-3808" data-mp="2tv0o3947yngz">
                          <span className="text-xs font-mono text-slate-400" data-id="element-3809" data-mp="2yu3twvs0wxq0">
                            {card.number}
                          </span>
                          <div className="w-6 h-6 rounded-full bg-seform-navy/10 flex items-center justify-center text-[10px] font-bold text-seform-navy" data-id="element-3810" data-mp="2k3wdellktory">
                            AL
                          </div>
                        </div>
                        <h4 className="font-semibold text-slate-900 text-sm mb-1 line-clamp-1" data-id="element-3811" data-mp="2f64lvsoug4oa">
                          {card.client}
                        </h4>
                        <p className="text-xs text-slate-500 mb-2 line-clamp-1" data-id="element-3812" data-mp="26680q11nxnxz">
                          {card.product}
                        </p>
                        <div className="flex items-center justify-between" data-id="element-3813" data-mp="38icdbtj7ac15">
                          <span className="text-sm font-mono font-medium text-seform-navy" data-id="element-3814" data-mp="34hsfenqsz4dy">
                            {card.amount}
                          </span>
                          <span className="text-xs text-slate-400" data-id="element-3815" data-mp="2zozekayggg75">
                            {card.expiry}
                          </span>
                        </div>
                      </div>)}
                    {cards.length === 0 && <div className="text-center py-8 text-slate-400 text-sm" data-id="element-3816" data-mp="2ksi3lku6h3l4">
                        Nessun elemento
                      </div>}
                  </div>
                </div>;
        })}
          </div>
        </div>}

      {/* View Modal */}
      <Modal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} title="Dettaglio contratto" size="2xl" data-id="element-3817" data-mp="f0bm2yk7x3bh">
        {selectedItem && <div className="space-y-8" data-id="element-3818" data-mp="hkzugq5neeb1">
            <section data-id="element-3819" data-mp="mkm6wxdnjqis">
              <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-3820" data-mp="mk2m98wmao12">
                <FileText className="h-4 w-4" data-id="element-3821" data-mp="rcdowjiqsnxb" /> Dati generali
              </h4>
              <div className="grid grid-cols-2 gap-4" data-id="element-3822" data-mp="waxectwjohuf">
                <div data-id="element-3823" data-mp="wci4867wzcly">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3824" data-mp="3us1btcn8u2tn">
                    Codice
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-3825" data-mp="3mt2trralajt">
                    {selectedItem.number}
                  </p>
                </div>
                <div data-id="element-3826" data-mp="8n7dzio6x0m2">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3827" data-mp="cm6hqzy6qrbx">
                    Cliente
                  </span>
                  <p className="font-medium mt-1" data-id="element-3828" data-mp="1jz8719s37b7m">{selectedItem.client}</p>
                </div>
                <div data-id="element-3829" data-mp="1o0a6nrpx1pa4">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3830" data-mp="1nzs4kegkogua">
                    Prodotto
                  </span>
                  <p className="font-medium mt-1" data-id="element-3831" data-mp="3g3eihje6xobi">{selectedItem.product}</p>
                </div>
                <div data-id="element-3832" data-mp="3u2npbsgib62t">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3833" data-mp="3u35ujel5cumv">
                    Tipologia
                  </span>
                  <p className="font-medium mt-1" data-id="element-3834" data-mp="3p4m34ynsaqn4">
                    {selectedItem.tipologia || '-'}
                  </p>
                </div>
                <div data-id="element-3835" data-mp="2xd6kffpclo26">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3836" data-mp="2xdohufnqy8bo">
                    Responsabile sviluppo
                  </span>
                  <p className="font-medium mt-1" data-id="element-3837" data-mp="36dbhox0tv62m">
                    {selectedItem.responsabileSviluppo || 'Alessio'}
                  </p>
                </div>
                <div data-id="element-3838" data-mp="2im646oid0pvh">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3839" data-mp="2ilo0j6tdb9dj">
                    Provenienza
                  </span>
                  <p className="font-medium mt-1" data-id="element-3840" data-mp="t6d8kvjn22h1">
                    {selectedItem.provenienza || '-'}
                  </p>
                </div>
                <div data-id="element-3841" data-mp="xymxq9cxiyi9">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3842" data-mp="k5x8lw23tdgx">
                    Corrispettivo provenienza
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-3843" data-mp="p5t9hmfi8os9">
                    {selectedItem.corrispettivoProvenienza || '-'}
                  </p>
                </div>
              </div>
            </section>

            <div className="h-px bg-slate-100" data-id="element-3844" data-mp="1gvelauzi7zs4"></div>

            <section data-id="element-3845" data-mp="12wy58dfr4wxb">
              <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-3846" data-mp="12vn0aaaklj4m">
                <AlertTriangle className="h-4 w-4" data-id="element-3847" data-mp="17tok4xftlmh5" /> Stato e workflow
              </h4>
              <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-3848" data-mp="3nl2nkvakyppi">
                <div data-id="element-3849" data-mp="3sm9wixogluxr">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3850" data-mp="3sms3ao8qzrjx">
                    Stato attuale
                  </span>
                  <div className="mt-1" data-id="element-3851" data-mp="o1ksneu53e6e">
                    <Badge variant={['accettato', 'accepted'].includes(selectedItem.status) ? 'success' : ['in_valutazione', 'pending', 'da_sollecitare', 'urgent'].includes(selectedItem.status) ? 'warning' : ['non_accettato', 'rejected', 'revocato', 'revoked'].includes(selectedItem.status) ? 'danger' : 'neutral'} data-id="element-3852" data-mp="j4kvxuj6jnry">
                      {statusLabels[selectedItem.status] || selectedItem.status}
                    </Badge>
                  </div>
                </div>
                <div data-id="element-3853" data-mp="1x68uoc7ed3iw">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3854" data-mp="222zbx2uxu55w">
                    Data cambio stato
                  </span>
                  <p className="font-medium mt-1" data-id="element-3855" data-mp="2746gyu5p4q8v">
                    {selectedItem.dataCambioStato || selectedItem.date || '15/03/2025'}
                  </p>
                </div>
              </div>

              {/* Conditional status-specific fields */}
              {['accettato', 'accepted'].includes(selectedItem.status) && <div className="bg-green-50 p-4 rounded-lg border border-green-100" data-id="element-3856" data-mp="2prrzaaxhv4ow">
                  <h4 className="text-sm font-bold text-green-800 uppercase tracking-wider mb-3" data-id="element-3857" data-mp="2usz9egn40fyq">
                    Dettagli accettazione
                  </h4>
                  <div className="grid grid-cols-2 gap-4" data-id="element-3858" data-mp="5dj1cfdq2kw9">
                    <div data-id="element-3859" data-mp="5by6sprer1ye">
                      <span className="text-xs font-semibold text-green-600 uppercase" data-id="element-3860" data-mp="5fvsnj5de7lz">
                        Data di accettazione
                      </span>
                      <p className="font-medium text-green-900" data-id="element-3861" data-mp="3sjk1vedplng4">
                        {selectedItem.acceptanceDate || '15/03/2025'}
                      </p>
                    </div>
                  </div>
                </div>}

              {['non_accettato', 'rejected'].includes(selectedItem.status) && <div className="bg-red-50 p-4 rounded-lg border border-red-100" data-id="element-3862" data-mp="t57yc91hnxjj">
                  <h4 className="text-sm font-bold text-red-800 uppercase tracking-wider mb-3" data-id="element-3863" data-mp="fb77s2koxgu9">
                    Dettagli non accettazione
                  </h4>
                  <div className="grid grid-cols-2 gap-4" data-id="element-3864" data-mp="172msfr2osthu">
                    <div data-id="element-3865" data-mp="1711yo3o75el1">
                      <span className="text-xs font-semibold text-red-600 uppercase" data-id="element-3866" data-mp="1bu4u6hcjay3v">
                        Data non accettazione
                      </span>
                      <p className="font-medium text-red-900" data-id="element-3867" data-mp="2whxifwsuvqbp">
                        {selectedItem.rejectionDate || '20/02/2025'}
                      </p>
                    </div>
                    <div data-id="element-3868" data-mp="369ac8ty7sqkd">
                      <span className="text-xs font-semibold text-red-600 uppercase" data-id="element-3869" data-mp="36bo1gm67r8k0">
                        Motivazione
                      </span>
                      <p className="font-medium text-red-900" data-id="element-3870" data-mp="2dm7dc0pmmdtl">
                        {selectedItem.rejectionReason || 'Budget insufficiente'}
                      </p>
                    </div>
                  </div>
                </div>}

              {['revocato', 'revoked'].includes(selectedItem.status) && <div className="bg-orange-50 p-4 rounded-lg border border-orange-100" data-id="element-3871" data-mp="2iixw4vstfnip">
                  <h4 className="text-sm font-bold text-orange-800 uppercase tracking-wider mb-3" data-id="element-3872" data-mp="2rjpkp2re464">
                    Dettagli revoca
                  </h4>
                  <div className="grid grid-cols-2 gap-4" data-id="element-3873" data-mp="4xq2b40zdjnw">
                    <div data-id="element-3874" data-mp="4zaw2rfh0ykp">
                      <span className="text-xs font-semibold text-orange-600 uppercase" data-id="element-3875" data-mp="9w1dbi30i07p">
                        Data revoca
                      </span>
                      <p className="font-medium text-orange-900" data-id="element-3876" data-mp="ew5tt83x6uxx">
                        {selectedItem.revokeDate || '10/01/2025'}
                      </p>
                    </div>
                    <div data-id="element-3877" data-mp="3idflnes9wgix">
                      <span className="text-xs font-semibold text-orange-600 uppercase" data-id="element-3878" data-mp="3ia06kbyln7hi">
                        Motivazione
                      </span>
                      <p className="font-medium text-orange-900" data-id="element-3879" data-mp="3sa0q9z0drpm8">
                        {selectedItem.revokeReason || 'Cambio fornitore'}
                      </p>
                    </div>
                  </div>
                </div>}
            </section>

            <div className="h-px bg-slate-100" data-id="element-3880" data-mp="34kg8vkjvl0f2"></div>

            <section data-id="element-3881" data-mp="34j56np62w0q2">
              <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-3882" data-mp="34ilp49kcbeck">
                <Euro className="h-4 w-4" data-id="element-3883" data-mp="1ho9oezvzo8gg" /> Dettagli economici
              </h4>
              <div className="grid grid-cols-2 gap-4" data-id="element-3884" data-mp="1hm5nmqtdzpke">
                <div data-id="element-3885" data-mp="1cnlzqkv8g3pe">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3886" data-mp="1cn2i759hvhbw">
                    Importo
                  </span>
                  <p className="font-mono font-bold text-seform-navy mt-1" data-id="element-3887" data-mp="12qhbmfhtty5y">
                    {selectedItem.amount}
                  </p>
                </div>
                <div data-id="element-3888" data-mp="xypvz007lcul">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3889" data-mp="xy6efkeh0qh3">
                    Durata (mesi)
                  </span>
                  <p className="font-medium mt-1" data-id="element-3890" data-mp="p0dvm69jj0oy">
                    {selectedItem.durata || '12'}
                  </p>
                </div>
                <div data-id="element-3891" data-mp="k2c9tdkqcn9s">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3892" data-mp="f1evjc2ucddk">
                    Data offerta
                  </span>
                  <p className="font-medium mt-1" data-id="element-3893" data-mp="2f4pgz5apf4er">
                    {selectedItem.dataPreventivo || selectedItem.date || '10/01/2025'}
                  </p>
                </div>
                <div data-id="element-3894" data-mp="25big9xqwscl0">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3895" data-mp="2aco8zvkkjzuj">
                    Scadenza
                  </span>
                  <p className="font-medium mt-1" data-id="element-3896" data-mp="1wbvcx6emcwpb">{selectedItem.expiry}</p>
                </div>
                <div data-id="element-3897" data-mp="21d17l9ruau1h">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3898" data-mp="21dkp4pdkvgez">
                    Tacito rinnovo
                  </span>
                  <p className="font-medium mt-1" data-id="element-3899" data-mp="1rdtrietq0lao">
                    {selectedItem.tacitRenewal !== false ? 'Sì' : 'No'}
                  </p>
                </div>
              </div>
            </section>

            <div className="h-px bg-slate-100" data-id="element-3900" data-mp="3lrl0rd85135l"></div>

            {/* Attività operative section - NEW */}
            <section data-id="element-3901" data-mp="1a3yd1lb4jeh6">
              <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-3902" data-mp="1a5j3ayqeob98">
                <ClipboardList className="h-4 w-4" data-id="element-3903" data-mp="1577wkwveqgv8" /> Attività operative
              </h4>
              {(() => {
            const contractActivities = operativeActivitiesStore.getByContract(selectedItem.number);
            if (contractActivities.length === 0) {
              return <div className="text-center py-6 bg-slate-50 rounded-lg border border-slate-100" data-id="element-3904" data-mp="1swtwxnnin24t">
                      <ClipboardList className="h-6 w-6 mx-auto mb-2 text-slate-300" data-id="element-3905" data-mp="1ovqi94h7fc56" />
                      <p className="text-sm text-slate-400" data-id="element-3906" data-mp="22r2itsi1jwu0">
                        Nessuna attività operativa generata per questo contratto
                      </p>
                    </div>;
            }
            return <div className="space-y-3" data-id="element-3907" data-mp="3h2u1fliljre">
                    {contractActivities.map(act => {
                const prog = getProgress(act);
                return <div key={act.id} onClick={() => {
                  setSelectedOperativeActivity(act);
                  setIsActivityDetailOpen(true);
                }} className={`p-4 rounded-lg border cursor-pointer transition-all hover:shadow-sm ${prog.isOverdue ? 'bg-red-50 border-red-200 hover:bg-red-50/80' : prog.isCompleted ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200 hover:bg-white'}`} data-id="element-3908" data-mp="28glqca0jlhty">
                          <div className="flex items-center justify-between" data-id="element-3909" data-mp="2w9c14fwplv2n">
                            <div data-id="element-3910" data-mp="307rssx3qjrme">
                              <h5 className="font-semibold text-sm text-slate-900" data-id="element-3911" data-mp="357wcsx3ur4hd">
                                {act.title}
                              </h5>
                              <p className="text-xs text-slate-500 mt-0.5" data-id="element-3912" data-mp="3a70j9fze6o40">
                                Operatore: {act.operator} · Scadenza:{' '}
                                {new Date(act.deadline).toLocaleDateString('it-IT')}
                              </p>
                            </div>
                            <div className="w-40" data-id="element-3913" data-mp="36by3uh55socx">
                              <ActivityProgressBadge currentStep={prog.currentStep} totalSteps={prog.totalSteps} percentage={prog.percentage} isCompleted={prog.isCompleted} isOverdue={prog.isOverdue} size="sm" data-id="element-3914" data-mp="6vrqasj7gqr1" />
                            </div>
                          </div>
                        </div>;
              })}
                  </div>;
          })()}
            </section>

            <div className="flex justify-between items-center pt-4 border-t border-slate-100" data-id="element-3915" data-mp="14it0vw8fz85s">
              <div data-id="element-3916" data-mp="19bcntjt0rfn9">
                {['accettato', 'accepted'].includes(selectedItem.status) && <Button variant="flat" color="success" startContent={<ClipboardList className="h-4 w-4" data-id="element-3918" data-mp="zkzvh5eyqi6k" />} onClick={() => {
              setIsViewModalOpen(false);
              navigate('/operations', {
                state: {
                  openWizard: true,
                  contractNumber: selectedItem?.number,
                  clientName: selectedItem?.client
                }
              });
            }} data-id="element-3917" data-mp="umq0ovx5hjlh">
                    Genera attività operativa
                  </Button>}
              </div>
              <div className="flex gap-3" data-id="element-3919" data-mp="6wm0yzn323zr">
                <Button variant="flat" color="default" onClick={() => setIsViewModalOpen(false)} data-id="element-3920" data-mp="2l3lg2u0hcd6r">
                  Chiudi
                </Button>
                <PermissionGate module="contracts" action="edit" data-id="element-3921" data-mp="2u4s03d2c864r">
                  <Button onClick={() => {
                setIsViewModalOpen(false);
                handleEdit(selectedItem);
              }} startContent={<Edit className="h-4 w-4" data-id="element-3923" data-mp="22azmkgd1oypc" />} data-id="element-3922" data-mp="2u1xhzs6sqxei">
                    Modifica
                  </Button>
                </PermissionGate>
              </div>
            </div>
          </div>}
      </Modal>

      {/* Generate Activity Modal - Updated with props */}
      <GenerateActivityWizardModal isOpen={isGenerateActivityOpen} onClose={() => setIsGenerateActivityOpen(false)} contractNumber={selectedItem?.number} clientName={selectedItem?.client} data-id="element-3924" data-mp="26de5f0gprygm" />

      {/* Activity Detail Modal */}
      <ActivityDetailModal isOpen={isActivityDetailOpen} onClose={() => setIsActivityDetailOpen(false)} activity={selectedOperativeActivity} onStepComplete={handleStepComplete} onEdit={handleEditActivityFromContract} data-id="element-3925" data-mp="1nvlrx6znbn90" />

      {/* Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedItem ? 'Modifica contratto' : 'Nuova offerta'} size="2xl" data-id="element-3926" data-mp="1svgi18blk8t0">
        <AnimatePresence mode="wait" data-id="element-3927" data-mp="107km45tuqkxa">
          {saveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center py-16" data-id="element-3928" data-mp="1057ut227ey7k">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-3929" data-mp="hh2uy5oo3xe1">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-3930" data-mp="2o53a41gsstzz" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-3931" data-mp="3buxl0dhk48hl">
                Contratto salvato con successo
              </p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-3932" data-mp="32sobnqy0yl5s">
              <div className="space-y-8" data-id="element-3933" data-mp="1rdhsqidod4ng">
                <section data-id="element-3934" data-mp="1rgnk649s0imc">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-3935" data-mp="1vfwdx94tgghw">
                    <FileText className="h-4 w-4" data-id="element-3936" data-mp="1wdw2xcw7nll" /> Dati generali
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3937" data-mp="3t36z86r8xwsr">
                    <ProgressiveNumberInput type="contract" value={selectedItem?.number} onChange={() => {}} disabled={false} data-id="element-3938" data-mp="3od08a099i07c" />
                    <SearchableSelect label="Cliente" value={selectedItem?.client || ''} onChange={() => {}} options={[{
                  value: 'Azienda 0 SRL',
                  label: 'Azienda 0 SRL'
                }, {
                  value: 'Studio 1 Associati',
                  label: 'Studio 1 Associati'
                }, {
                  value: 'Impresa 2 SPA',
                  label: 'Impresa 2 SPA'
                }, {
                  value: 'Azienda 3 SRL',
                  label: 'Azienda 3 SRL'
                }]} required data-id="element-3939" data-mp="3fdd6hdcmesdr" />
                    <SearchableSelect label="Prodotto" value={offerProduct} onChange={e => handleProductSelection(e.target.value)} options={offerProductOptions} placeholder="Cerca e seleziona un prodotto..." required data-id="element-3940" data-mp="1kusai1905wxf" />
                    <Select label="Tipologia" value={offerType} onChange={e => setOfferType(e.target.value)} options={[{
                  value: '',
                  label: 'Seleziona...'
                }, ...offerProductTypes.map(type => ({
                  value: type,
                  label: type
                }))]} data-id="element-3941" data-mp="12657rpeatjn0" />
                    <Input label="Codice prodotto" value={offerProductCode} onChange={e => setOfferProductCode(e.target.value)} placeholder="Inserisci il codice prodotto" data-id="element-3942" data-mp="3vg19hkw881mb" />
                    <Select label="Ambito" value={offerScope} onChange={e => setOfferScope(e.target.value)} options={[{
                  value: '',
                  label: 'Seleziona...'
                }, ...offerScopes.map(scope => ({
                  value: scope,
                  label: scope
                }))]} data-id="element-3943" data-mp="4buhel3z2q24" />
                    <Select label="Responsabile sviluppo" options={[{
                  value: 'alessio',
                  label: 'Alessio'
                }, {
                  value: 'gianfranco',
                  label: 'Gian Franco'
                }, {
                  value: 'lorenzo',
                  label: 'Lorenzo'
                }, {
                  value: 'tiziano',
                  label: 'Tiziano'
                }]} required data-id="element-3944" data-mp="1bnqoscjkj56f" />
                    <Select label="Commissionato da:" defaultValue={selectedItem?.provenienza || ''} options={[{
                  value: '',
                  label: 'Seleziona...'
                }, {
                  value: 'Commerciale SEF',
                  label: 'Commerciale SEF'
                }, {
                  value: 'Esterni e partner',
                  label: 'Esterni e partner'
                }, {
                  value: 'Altro',
                  label: 'Altro'
                }]} data-id="element-3945" data-mp="90m36tdzqou5" />
                    <Input label="Corrispettivo provenienza (€)" placeholder="Es. 500,00" defaultValue={selectedItem?.corrispettivoProvenienza?.replace('€ ', '') || ''} data-id="element-3946" data-mp="2gmwzca8kli7q" />
                  </div>
                </section>

                <div className="h-px bg-slate-100" data-id="element-3947" data-mp="3kwyspc74rt3u"></div>

                <section data-id="element-3948" data-mp="3pvif1dpmz6wq">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-3949" data-mp="3pu7bnf03s165">
                    <AlertTriangle className="h-4 w-4" data-id="element-3950" data-mp="3unjlgj04i3eu" /> Stato e workflow
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3951" data-mp="3f5qhrquapdi">
                    <Select label="Stato attuale" value={editFormStatus} onChange={e => setEditFormStatus(e.target.value)} options={[{
                  value: 'da_proporre',
                  label: 'Da proporre'
                }, {
                  value: 'in_valutazione',
                  label: 'In valutazione'
                }, {
                  value: 'da_sollecitare',
                  label: 'Da sollecitare'
                }, {
                  value: 'accettato',
                  label: 'Accettato'
                }, {
                  value: 'non_accettato',
                  label: 'Non accettato'
                }, {
                  value: 'revocato',
                  label: 'Revocato'
                }]} required data-id="element-3952" data-mp="8dpfk10qcha3" />
                    <Input label="Data dell'offerta" type="date" value={editFormStatus === 'accettato' ? editFormAcceptanceDate : new Date().toISOString().split('T')[0]} onChange={() => {}} data-id="element-3953" data-mp="ay59vxeawgwv" />

                    {editFormStatus === 'accettato' && <Input label="Data di accettazione" type="date" value={editFormAcceptanceDate} onChange={e => setEditFormAcceptanceDate(e.target.value)} data-id="element-3954" data-mp="2vba9ozuzwpto" />}

                    {editFormStatus === 'non_accettato' && <>
                        <Input label="Data non accettazione" type="date" value={editFormRejectionDate} onChange={e => setEditFormRejectionDate(e.target.value)} data-id="element-3955" data-mp="3e9393s6euhmb" />
                        <div className="col-span-full" data-id="element-3956" data-mp="flz76h2xf04t">
                          <Input label="Motivazione" placeholder="Inserisci la motivazione..." value={editFormRejectionReason} onChange={e => setEditFormRejectionReason(e.target.value)} data-id="element-3957" data-mp="1hjdmym4wcsqi" />
                        </div>
                      </>}

                    {editFormStatus === 'revocato' && <>
                        <Input label="Data revoca" type="date" value={editFormRevokeDate} onChange={e => setEditFormRevokeDate(e.target.value)} data-id="element-3958" data-mp="17lz4yi3brvw3" />
                        <div className="col-span-full" data-id="element-3959" data-mp="2nzfuqyiri1fv">
                          <Input label="Motivazione" placeholder="Inserisci la motivazione della revoca..." value={editFormRevokeReason} onChange={e => setEditFormRevokeReason(e.target.value)} data-id="element-3960" data-mp="2o222ay5nkgy3" />
                        </div>
                      </>}
                  </div>
                </section>

                <div className="h-px bg-slate-100" data-id="element-3961" data-mp="2aeuwpp4n3f70"></div>

                <section data-id="element-3962" data-mp="2ada25zibrw95">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-3963" data-mp="2agxu4u84galr">
                    <Euro className="h-4 w-4" data-id="element-3964" data-mp="21eeueizmue3q" /> Dettagli economici
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3965" data-mp="3nveyrqr9t2o">
                    <Input label="Importo (€)" type="number" placeholder="0.00" defaultValue={selectedItem?.amount?.replace('€ ', '')} required data-id="element-3966" data-mp="3lssuisq4k38" />
                    <Input label="Durata (mesi)" type="number" defaultValue="12" required data-id="element-3967" data-mp="1xxk3v9uspf5y" />
                    <Input label="Data offerta" type="date" defaultValue="2025-01-10" data-id="element-3968" data-mp="1o6pl4fgrcxp9" />
                    <div className="flex items-center gap-2 pt-6" data-id="element-3969" data-mp="2gtscmnn0o860">
                      <input type="checkbox" id="renewal" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" defaultChecked={selectedItem?.tacitRenewal ?? false} data-id="element-3970" data-mp="22vc7h14syppz" />
                      <label htmlFor="renewal" className="text-sm text-slate-700" data-id="element-3971" data-mp="wfjedg6j81r0">
                        Tacito rinnovo
                      </label>
                    </div>
                  </div>
                </section>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-3972" data-mp="xwp3jo003ene">
                  <Button variant="flat" color="default" onClick={() => setIsModalOpen(false)} data-id="element-3973" data-mp="sxc5e73oil5i">
                    Annulla
                  </Button>
                  <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-3975" data-mp="extbojbm7ccq" />} data-id="element-3974" data-mp="9g9h4lxox9s">
                    Salva contratto
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} title="Conferma eliminazione" size="sm" data-id="element-3976" data-mp="3dnl0lwedwna4">
        <AnimatePresence mode="wait" data-id="element-3977" data-mp="2gxrachavppsu">
          {deleteSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3
        }} className="flex flex-col items-center justify-center py-12" data-id="element-3978" data-mp="2lw2i8mhm5q8f">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 150,
            damping: 15,
            delay: 0.1
          }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-3979" data-mp="34jxs3va3ujw9">
                <CheckCircle2 className="w-10 h-10 text-green-500" data-id="element-3980" data-mp="1bowtm4thtnjg" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-3981" data-mp="16xew5qqoi538">
                Eliminato con successo
              </p>
            </motion.div> : <motion.div key="confirm" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-3982" data-mp="11whc18m3z0z3">
              <div className="space-y-6" data-id="element-3983" data-mp="1ul501by8s4gc">
                <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex gap-3" data-id="element-3984" data-mp="1pphd7378fne0">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-3985" data-mp="1lm1iiceq3x3y" />
                  <div data-id="element-3986" data-mp="1gnzy9o5k9rqw">
                    <p className="text-sm text-red-700 font-medium" data-id="element-3987" data-mp="1gmomb11ypdop">
                      Attenzione: questa azione è irreversibile.
                    </p>
                    <p className="text-sm text-red-600 mt-1" data-id="element-3988" data-mp="58krxtrgpnj4">
                      Il contratto <strong data-id="element-3989" data-mp="579s25150zxa">{itemToDelete?.number}</strong> verrà
                      eliminato definitivamente.
                    </p>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4" data-id="element-3990" data-mp="bob7zaeam7ra">
                  <Button variant="flat" color="default" onClick={() => setShowDeleteDialog(false)} data-id="element-3991" data-mp="3tuzcoz5fkswf">
                    Annulla
                  </Button>
                  <Button color="danger" onClick={confirmDelete} startContent={<Trash2 className="h-4 w-4" data-id="element-3993" data-mp="3oxg003odso6l" />} data-id="element-3992" data-mp="3k31zxnyrxsvl">
                    Elimina
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>
    </Layout>;
}